"use client"

import Image from "next/image"
import type { CSSProperties } from "react"

import { Icon, Typography } from "@/shared/ui/atoms"
import { formatFivePointRating } from "@/entities/hospital/lib/rating"

export interface HospitalResultCardContentProps {
  name: string
  address: string
  categoryLabel?: string
  score: number
  reviewCount: number
  imageUrl?: string
  searchDesign?: boolean
  highlight?: string
}

const thumbnailSizeStyle = { "--width": 80, "--height": 80 } as CSSProperties

const HospitalResultCardContent = ({
  name,
  address,
  categoryLabel,
  score,
  reviewCount,
  imageUrl,
  searchDesign = false,
  highlight = "",
}: HospitalResultCardContentProps) => {
  // 리뷰가 있을 때만 평점을 노출하므로 환산 분기도 그 경우만 남는다.
  const formattedScore = formatFivePointRating(score)
  const formattedReviewCount = new Intl.NumberFormat().format(reviewCount)
  const match = highlight ? name.toLocaleLowerCase().indexOf(highlight.toLocaleLowerCase()) : -1

  if (searchDesign) return <div className="search-hospital-content">
    <div className="search-hospital-text">
      <h2>{match < 0 ? name : <>{name.slice(0, match)}<mark>{name.slice(match, match + highlight.length)}</mark>{name.slice(match + highlight.length)}</>}</h2>
      <p>{address}{categoryLabel && <><span className="search-hospital-divider" aria-hidden />{categoryLabel}</>}</p>
      <div className="search-hospital-rating">{reviewCount > 0 ? <>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/images/home-v2/search-rating.svg" alt="" width={16} height={16} />
        <span className="sr-only">평점</span><strong>{formattedScore}</strong><span>({formattedReviewCount})</span><span className="sr-only">개 리뷰</span>
      </> : <span>리뷰 없음</span>}</div>
    </div>
    <div className="search-hospital-image">
      {imageUrl ? (
        <Image src={imageUrl} alt="" fill sizes="58px" className="object-cover" />
      ) : (
        <Image src="/images/home-v2/search-hospital.svg" alt="" width={36} height={36} />
      )}
    </div>
  </div>

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <div className="flex min-h-20 min-w-0 flex-1 flex-col self-stretch py-1">
        <div className="flex min-h-0 flex-1 flex-col justify-between gap-1">
          <div className="flex min-w-0 flex-col gap-1">
            <Typography
              size={18}
              lineHeight={22}
              whitespace="nowrap"
              className="min-w-0 truncate font-bold text-[#3A3A42]"
            >
              {name}
            </Typography>

            <div className="max-w-full self-start">
              <div className="inline-flex max-w-full items-center gap-1.5">
                <Typography
                  as="span"
                  size={14}
                  lineHeight={22}
                  whitespace="normal"
                  className="min-w-0 break-all text-[#46474C]"
                >
                  {address}
                </Typography>
                {categoryLabel ? (
                  <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap">
                    <span
                      className="dynamic-size shrink-0 bg-[#E1E2E4]"
                      style={{ "--width": 1, "--height": 14 } as CSSProperties}
                      aria-hidden
                    />
                    <Typography as="span" size={14} lineHeight={22} whitespace="nowrap" className="text-[#46474C]">
                      {categoryLabel}
                    </Typography>
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-0.5">
            {/* 리뷰가 한 건도 없으면 자리표시자 0을 평점처럼 보여주지 않는다.
                "0점짜리 병원"과 "아직 평가가 없는 병원"은 전혀 다른 사실이다. */}
            {reviewCount > 0 ? (
              <>
                <Icon src="/icons/star.svg" alt="" width={18} height={18} className="shrink-0" />
                <div className="flex items-center gap-[3px]">
                  {/* 별 아이콘은 alt가 비어 있어 숫자만 읽힌다. 스크린리더와 본문을 인용하는
                      AI 검색이 숫자의 정체를 알 수 있게 라벨을 시각적으로만 숨겨 둔다. */}
                  <span className="sr-only">평점</span>
                  <Typography size={16} lineHeight={22} className="font-semibold text-[#333438]">
                    {formattedScore}
                  </Typography>
                  <span className="sr-only">, 리뷰</span>
                  <Typography size={14} lineHeight={22} className="text-[#AEB0B6]">
                    ({formattedReviewCount})
                  </Typography>
                  <span className="sr-only">개</span>
                </div>
              </>
            ) : (
              <Typography size={14} lineHeight={22} className="text-[#AEB0B6]">
                리뷰 없음
              </Typography>
            )}
          </div>
        </div>
      </div>

      {imageUrl ? (
        <div
          className="relative shrink-0 dynamic-size min-h-[80px] min-w-[80px] overflow-hidden rounded-lg bg-[#E5E5EA]"
          style={thumbnailSizeStyle}
        >
          <Image src={imageUrl} alt="" fill className="object-cover" sizes="(max-width: 600px) 21.33vw, 128px" />
        </div>
      ) : null}
    </div>
  )
}

export default HospitalResultCardContent
